import React, { useEffect, useState } from 'react';
import { getStoredToken } from '../../utils/tokenStorage';
import { formatDateTime } from '../../utils/dateFormat';

interface AccessSession {
  started_at: string | null;
  duration_minutes: number | null;
}

interface MyAccessData {
  filename: string;
  shared_by: string;
  organisation: string;
  permission: string;
  verified_at: string | null;
  expires_at: string | null;
  shared_at: string | null;
  sessions: AccessSession[];
}

interface SecureShareBannerProps {
  shareToken: string;
  sharedByName?: string;
  permission?: string;
}

const SecureShareBanner: React.FC<SecureShareBannerProps> = ({ shareToken, sharedByName, permission }) => {
  const [accessData, setAccessData] = useState<MyAccessData | null>(null);
  const [showAccessLog, setShowAccessLog] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getStoredToken();
    fetch(`/api/v1/shared/${shareToken}/my-access`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data) setAccessData(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [shareToken]);

  if (loading || !accessData) {
    // Fallback banner with navigation state data
    if (!sharedByName) return null;
    return (
      <div className="px-4 py-2 bg-slate-800 border-b border-slate-700 flex items-center gap-3">
        <svg className="w-4 h-4 text-teal-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        <span className="text-sm text-slate-300">
          Securely shared by <span className="text-white font-medium">{sharedByName}</span>
          {permission && <span className="text-slate-400"> &middot; {permission === 'view' ? 'View only' : permission}</span>}
        </span>
      </div>
    );
  }

  const permLabel = accessData.permission === 'view' ? 'View only' : accessData.permission === 'edit' ? 'Edit' : accessData.permission;

  return (
    <>
      <div className="px-4 py-2 bg-slate-800 border-b border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <svg className="w-4 h-4 text-teal-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <div className="text-sm">
            <span className="text-slate-300">
              Securely shared by{' '}
              <span className="text-white font-medium">{accessData.shared_by}</span>
              {accessData.organisation && (
                <span className="text-slate-400"> ({accessData.organisation})</span>
              )}
              <span className="text-slate-400"> &middot; {permLabel}</span>
            </span>
            <span className="text-slate-500 text-xs block">
              Your access is logged &middot; Watermarked to your identity
              {accessData.expires_at ? (
                <> &middot; Expires: {formatDateTime(accessData.expires_at)}</>
              ) : (
                <> &middot; Expires: Never</>
              )}
              {accessData.shared_at && (
                <> &middot; Shared: {formatDateTime(accessData.shared_at)}</>
              )}
            </span>
          </div>
        </div>
        <button
          onClick={() => setShowAccessLog(!showAccessLog)}
          className="text-xs text-teal-400 hover:text-teal-300 transition-colors flex items-center gap-1"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          My Access Log
        </button>
      </div>

      {/* Access Log Panel */}
      {showAccessLog && (
        <div className="bg-slate-850 border-b border-slate-700 px-4 py-3" style={{ backgroundColor: 'var(--card-bg)' }}>
          <div className="max-w-lg">
            <h4 className="text-sm font-medium text-white mb-2">
              Your access to '{accessData.filename}'
            </h4>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-slate-400 mb-3">
              <div>Shared by: <span className="text-slate-300">{accessData.shared_by}{accessData.organisation && ` (${accessData.organisation})`}</span></div>
              <div>Permission: <span className="text-slate-300">{permLabel}</span></div>
              {accessData.verified_at && (
                <div>Verified: <span className="text-slate-300">{formatDateTime(accessData.verified_at)}</span></div>
              )}
              <div>Expires: <span className="text-slate-300">{accessData.expires_at ? formatDateTime(accessData.expires_at) : 'Never'}</span></div>
            </div>

            {accessData.sessions.length > 0 ? (
              <div>
                <p className="text-xs text-slate-500 mb-1.5">Your sessions:</p>
                <div className="space-y-1">
                  {accessData.sessions.slice(0, 10).map((s, i) => (
                    <div key={i} className="text-xs text-slate-400 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-teal-500/50 flex-shrink-0" />
                      {s.started_at ? formatDateTime(s.started_at) : 'Unknown'}
                      {s.duration_minutes !== null && (
                        <span className="text-slate-500">
                          &mdash; {s.duration_minutes} minute{s.duration_minutes !== 1 ? 's' : ''}
                        </span>
                      )}
                      {i === accessData.sessions.length - 1 && (
                        <span className="text-slate-600">(first access)</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-xs text-slate-500">No previous sessions recorded.</p>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SecureShareBanner;
